import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

// thai-production build: Thailand is the only region. Other components import
// this list, so keeping it to a single entry removes every other country from
// the app's region pickers.
export const REGION_OPTIONS = [{ value: "healjai", label: "ประเทศไทย" }];

// Fixed for this deployment — there is no region or language choice.
const THAI_REGION = "healjai";
const THAI_LANGUAGE = "th";

const Register: React.FC = () => {
  // The landing page profile form can hand over the visitor's first name.
  const location = useLocation();
  const prefillUsername = (location.state as { prefillUsername?: string } | null)
    ?.prefillUsername;
  const [username, setUsername] = useState(prefillUsername ?? "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mobileNo, setMobileNo] = useState("");
  const [roleId] = useState("2");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username.trim()) {
      toast.error("กรุณากรอกชื่อผู้ใช้");
      return;
    }

    if (!email.trim()) {
      toast.error("กรุณากรอกอีเมล");
      return;
    }

    if (!password.trim()) {
      toast.error("กรุณากรอกรหัสผ่าน");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("รหัสผ่านไม่ตรงกัน");
      return;
    }

    if (password.length < 6) {
      toast.error("รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร");
      return;
    }

    setIsLoading(true);

    try {
      await register(
        username,
        email,
        password,
        mobileNo || undefined,
        THAI_LANGUAGE,
        roleId,
        THAI_REGION,
      );
      toast.success("สมัครสมาชิกสำเร็จ!");
      navigate("/");
    } catch (error: any) {
      console.error("Registration error in component:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrength = (password: string) => {
    if (password.length === 0) return { strength: 0, text: "", color: "" };
    if (password.length < 6)
      return { strength: 25, text: "อ่อน", color: "bg-red-500" };
    if (password.length < 8)
      return { strength: 50, text: "พอใช้", color: "bg-yellow-500" };
    if (password.length < 10)
      return { strength: 75, text: "ดี", color: "bg-blue-500" };
    return { strength: 100, text: "แข็งแรง", color: "bg-green-500" };
  };

  const passwordStrength = getPasswordStrength(password);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0f0c29] via-[#302b63] to-[#24243e] px-4">
      <div className="w-full max-w-md bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-4xl mb-3">🚀</div>
          <h2 className="text-3xl font-extrabold text-white">สร้างบัญชี</h2>
          <p className="text-sm text-gray-300 mt-2">
            มีบัญชีอยู่แล้ว?{" "}
            <Link to="/login" className="text-purple-400 font-medium underline">
              เข้าสู่ระบบ
            </Link>
          </p>
        </div>

        {/* Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Username */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              ชื่อผู้ใช้ *
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="กรอกชื่อผู้ใช้"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">อีเมล *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="you@example.com"
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              เบอร์โทรศัพท์ (ไม่บังคับ)
            </label>
            <input
              type="tel"
              value={mobileNo}
              onChange={(e) => setMobileNo(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="+66 81 234 5678"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              รหัสผ่าน *
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? "🙈" : "👁️"}
              </button>
            </div>

            {password.length > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs text-gray-300 mb-1">
                  <span>ความแข็งแรงของรหัสผ่าน</span>
                  <span>{passwordStrength.text}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-700 rounded-full">
                  <div
                    className={`${passwordStrength.color} h-1.5 rounded-full transition-all`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm text-gray-300 mb-1">
              ยืนยันรหัสผ่าน *
            </label>
            <input
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white text-black focus:ring-2 focus:ring-purple-500 outline-none"
              placeholder="••••••••"
            />
            {confirmPassword && password !== confirmPassword && (
              <p className="text-xs text-red-400 mt-1">รหัสผ่านไม่ตรงกัน</p>
            )}
          </div>

          <input type="hidden" value={roleId} />

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold shadow-lg hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "กำลังสร้างบัญชี..." : "สร้างบัญชี"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
